import {useState, useEffect} from 'react';
import {getStore} from 'src/v2/state/store';
import {WalletCard} from 'src/v2/components/wallet/WalletCard';
import {BrandedButton} from 'src/v2/components/common';
import {CurrencySelector} from 'src/v2/components/pay/CurrencySelector';
import {WalletActionMenu} from 'src/v2/components/pay/WalletActionMenu';
import type {SupportedCurrency, Wallet} from 'src/v2/state/types';
import {useSetupPolling} from 'src/v2/components/setup/useSetupPolling';
import {SetupConfirmationPendingOverlay} from 'src/v2/components/setup/SetupConfirmationPendingOverlay';
import {useHandleSetup} from 'src/v2/components/setup/useHandleSetup';
import {SetupTerms} from 'src/v2/components/setup/SetupTerms';
import {WalletRegistry} from 'src/v2/util/WalletRegistry';
import {useNetworkSwitchingEffect} from 'src/v2/state/useNetworkSwitching';
import {
  CurrencyBalance,
  useCurrencyBalances,
} from 'src/v2/state/useCurrencyBalances';
import {ErrorBanner} from 'src/v2/components/error/ErrorBanner';
import {useStartPollingOnPageLoad} from 'src/v2/components/pay/useStartPollingOnPageLoad';
import {Banner} from 'src/v2/components/common/Banner';
import {AnimatePresence, motion} from '@sail/motion';
import {ScreenContentLayout} from 'src/v2/components/screen/ScreenContentLayout';

import {message, useIntl} from '@sail/intl';
import {view} from '@sail/ui';

const hasSufficientBalance = (
  {amountInUsdMinor}: CurrencyBalance,
  depositAmount: number,
  // Default to true if we can't get the balance either because it's loading or errored
) => amountInUsdMinor === null || amountInUsdMinor >= depositAmount;

const messages = {
  currencyLabel: message({
    id: 'crypto.payins.v2.setup_screen.currency_label',
    description: 'Label for currency selector',
    defaultMessage: 'Currency',
  }),

  setupText: message({
    id: 'crypto.payins.v2.setup_screen.setup_text',
    description: 'Label for button to save payment method',
    defaultMessage: 'Save and continue',
  }),

  processingText: message({
    id: 'crypto.payins.v2.setup_screen.processing_text',
    description: 'Label for button to save payment method',
    defaultMessage: 'Processing...',
  }),

  submittingOnChainText: message({
    id: 'crypto.payins.v2.setup_screen.submitting_on_chain_text',
    description: 'Label for button when submitting on chain',
    defaultMessage: 'Submitting on chain...',
  }),

  insufficientBalance: message<{
    selectedCurrency: React.ReactNode;
  }>({
    id: 'crypto.payins.v2.setup_screen.insufficient_balance',
    description: 'Label for insufficient balance',

    defaultMessage:
      'You must have at least 1 {selectedCurrency} in your wallet to save it as a payment method.',
  }),
};

interface Props {
  activeWallet: Wallet;
}

export const SetupScreen = ({activeWallet}: Props) => {
  const intl = useIntl();
  const {isPolling: isPollingSetup, startPolling: startPollingSetup} =
    useSetupPolling();
  const [isSubmittingSetup, setIsSubmittingSetup] = useState(false); // api loading
  const [buttonMessageKey, setButtonMessageKey] = useState<
    'setup' | 'processing' | 'submitting'
  >('setup');
  const handleSetup = useHandleSetup();
  const store = getStore();

  const showSetupConfirmationPendingOverlay = store.useSelector(
    (state) => state.overlay === 'CONFIRM_SETUP',
  );
  const sessionState = store.useSelector((state) => state.state);
  const isSessionTerminal =
    sessionState === 'failed' || sessionState === 'canceled';

  const {data: currencyBalances, loading: loadingBalances} =
    useCurrencyBalances();
  const [selectedCurrency, setSelectedCurrency] = useState<SupportedCurrency>(
    currencyBalances[0]!.currency,
  );

  // Sync selected currency to global store so PayTitle can display it
  useEffect(() => {
    store.setValue((state) => {
      state.selectedCurrency = selectedCurrency;
    });
  }, [selectedCurrency, store]);

  useNetworkSwitchingEffect();

  useStartPollingOnPageLoad({
    startPolling: startPollingSetup,
    isPolling: isPollingSetup,
  });

  const onClickSetup = async () => {
    await handleSetup({
      selectedCurrency,
      setIsProcessing: setIsSubmittingSetup,
      onConfirmSuccess: startPollingSetup,
    });
  };

  const walletOption = WalletRegistry.getWalletOption({
    provider: activeWallet.type,
    key: activeWallet.key,
  });

  if (!walletOption) {
    throw new Error('Wallet option not found in SetupScreen');
  }

  const isSetupProcessing = isSubmittingSetup || isPollingSetup;
  const isScreenInProgress = isSetupProcessing || loadingBalances;

  const selectedCurrencyBalance = currencyBalances.find(
    (c) => c.currency.id === selectedCurrency.id,
  );

  const insufficientBalance = !hasSufficientBalance(
    selectedCurrencyBalance!,
    100,
  );

  useEffect(() => {
    if (isPollingSetup) {
      // After 2.5 seconds, transition button text to "Submitting on chain..."
      const onChainTimer = setTimeout(() => {
        setButtonMessageKey('submitting');
      }, 2500);
      return () => {
        clearTimeout(onChainTimer);
      };
    } else if (isSetupProcessing) {
      setButtonMessageKey('processing');
    } else {
      setButtonMessageKey('setup');
    }
  }, [isSetupProcessing, isPollingSetup]);

  const buttonMessages = {
    setup: messages.setupText,
    processing: messages.processingText,
    submitting: messages.submittingOnChainText,
  };

  return (
    <>
      <ScreenContentLayout
        bottom={
          <>
            <BrandedButton
              disabled={
                isScreenInProgress || insufficientBalance || isSessionTerminal
              }
              onClick={onClickSetup}
              loading={isSetupProcessing}
            >
              <AnimatePresence initial={false} mode="wait">
                <view.div
                  key={buttonMessageKey}
                  uses={[
                    motion({
                      initial: {opacity: 0},
                      animate: {opacity: 1},
                      exit: {opacity: 0},
                      transition: {duration: 0.25},
                    }),
                  ]}
                >
                  {intl.formatMessage(buttonMessages[buttonMessageKey])}
                </view.div>
              </AnimatePresence>
            </BrandedButton>
            <SetupTerms />
          </>
        }
      >
        <WalletCard
          wallet={{
            ...activeWallet,
            option: walletOption,
          }}
          headerActions={
            <WalletActionMenu
              disabled={isSetupProcessing || isSessionTerminal}
            />
          }
          bottomContent={
            <CurrencySelector
              selectedCurrencyId={selectedCurrency.id}
              onCurrencySelect={(id) => {
                const currencyBalance = currencyBalances.find(
                  (c) => c.currency.id === id,
                );
                if (!currencyBalance) return;
                setSelectedCurrency(currencyBalance.currency);
              }}
              label={intl.formatMessage(messages.currencyLabel)}
              currencyBalances={currencyBalances}
              disabled={isScreenInProgress || isSessionTerminal}
            />
          }
        />
        {insufficientBalance ? (
          <Banner
            message={intl.formatMessage(messages.insufficientBalance, {
              selectedCurrency: selectedCurrency.assetDisplayName,
            })}
            variant="error"
          />
        ) : null}
        <ErrorBanner />
      </ScreenContentLayout>
      {showSetupConfirmationPendingOverlay ? (
        <SetupConfirmationPendingOverlay />
      ) : null}
    </>
  );
};
